/** @jsx jsx */
import { jsx, useThemeUI } from "theme-ui"
import { useState, useEffect } from "react"
import { SearchIcon } from "../../assets/icons"
import { ab } from "../../theme"
import Markdown from "react-markdown"
import ProductDataEntries from "./product-data-entries"

const DatasourceSearch = ({
  title,
  description,
  entries = [],
  loading = false,
  error = null,
  children,
  productPerPage,
  tableHeaderProduct = "Produkt",
  tableHeaderKennzeichnung = "Kennzeichnung",
  tableHeaderCertificate = "Zertifikat",
  tableHeaderDate = "Eingangsdatum",
  searchProductPlaceholder = "Nach Produkt suchen",
  searchKennzeichnungPlaceholder = "Nach Kennzeichnung suchen",
  noResultsText = "Keine Einträge gefunden.",
}) => {
  const { rawColors: colors } = useThemeUI().theme

  const [productSearchTerm, setProductSearchTerm] = useState("")
  const [kennzeichnungSearchTerm, setArticleSearchTerm] = useState("")
  const [filteredEntries, setFilteredEntries] = useState([])
  const [productInput, setProductInput] = useState({
    value: searchProductPlaceholder,
  })
  const [kennzeichnungInput, setArticleInput] = useState({
    value: searchKennzeichnungPlaceholder,
  })

  // Alphabetical comparator (German collation, natural numeric ordering so
  // charge numbers like "L 01/23" sort before "L 02/24"). Sorts by product
  // name first, then by Kennzeichnung.
  const compareAlphabetically = (a, b) =>
    (a.name || "").localeCompare(b.name || "", "de", {
      sensitivity: "base",
      numeric: true,
    }) ||
    (a.kennzeichnung || "").localeCompare(b.kennzeichnung || "", "de", {
      numeric: true,
    })

  // Filter entries based on both search terms
  useEffect(() => {
    const productTerm = productSearchTerm.toLowerCase().trim()
    const kennzeichnungTerm = kennzeichnungSearchTerm.toLowerCase().trim()

    if (!productTerm && !kennzeichnungTerm) {
      // No search active: show every entry, sorted alphabetically.
      setFilteredEntries([...entries].sort(compareAlphabetically))
      return
    }

    const filtered = entries.filter((entry) => {
      // For product search - matches against name
      const matchesProduct =
        !productTerm ||
        (entry.name && entry.name.toLowerCase().includes(productTerm))

      // For Kennzeichnung search - matches against kennzeichnung
      const matchesKennzeichnung =
        !kennzeichnungTerm ||
        (entry.kennzeichnung &&
          entry.kennzeichnung.toLowerCase().includes(kennzeichnungTerm))

      // Entry must match both filters (if they are active)
      return matchesProduct && matchesKennzeichnung
    })

    // Rank closest matches first: entries whose field starts with the search
    // term outrank entries that merely contain it, so the best matches appear
    // at the top of the table instead of the bottom.
    const matchRank = (value, term) => {
      if (!term) return 2
      const field = (value || "").toLowerCase()
      if (field.startsWith(term)) return 0
      if (field.includes(term)) return 1
      return 2
    }

    const sorted = filtered
      .map((entry) => ({
        entry,
        score:
          matchRank(entry.name, productTerm) +
          matchRank(entry.kennzeichnung, kennzeichnungTerm),
      }))
      .sort(
        (a, b) =>
          a.score - b.score || compareAlphabetically(a.entry, b.entry)
      )
      .map((item) => item.entry)

    setFilteredEntries(sorted)
  }, [productSearchTerm, kennzeichnungSearchTerm, entries])

  // Handle product search input changes
  const handleProductInputChange = (e) => {
    setProductInput({ value: e.target.value })
    setProductSearchTerm(e.target.value)
  }

  // Handle article search input changes
  const handleKennzeichnungInputChange = (e) => {
    setArticleInput({ value: e.target.value })
    setArticleSearchTerm(e.target.value)
  }

  // Handle focus and blur for product search input
  const handleProductFocus = () => {
    if (productInput.value === searchProductPlaceholder) {
      setProductInput({ value: "" })
    }
  }

  const handleProductBlur = () => {
    if (productInput.value === "") {
      setProductInput({ value: searchProductPlaceholder })
    }
  }

  // Handle focus and blur for article search input
  const handleKennzeichnungFocus = () => {
    if (kennzeichnungInput.value === searchKennzeichnungPlaceholder) {
      setArticleInput({ value: "" })
    }
  }

  const handleKennzeichnungBlur = () => {
    if (kennzeichnungInput.value === "") {
      setArticleInput({ value: searchKennzeichnungPlaceholder })
    }
  }

  // Parse dimension JSON if it exists
  const parseDimension = (entry) => {
    if (!entry.dimension) return {}

    try {
      return JSON.parse(entry.dimension)
    } catch (e) {
      console.error("Error parsing dimension:", e)
      return {}
    }
  }

  return (
    <div className="datasource-search-container">
      <div
        className="datasource-search"
        sx={{
          px: ab({ _: 0, md: 0 }),
          pb: 5,
          color: "text",
        }}
      >
        {/* Title and Description Section */}
        <div
          className="title-description"
          sx={{
            px: ab({ _: 3, md: 5 }),
            pt: ab({ _: 3, md: 4 }),
          }}
        >
          {title && (
            <h2
              sx={{
                mb: 3,
                color: colors.modes["gutesiegel"].primary,
              }}
            >
              {title}
            </h2>
          )}

          {description && (
            <div sx={{ mb: 4 }}>
              <Markdown>{description}</Markdown>
            </div>
          )}

          {children && <div sx={{ mb: 4 }}>{children}</div>}
        </div>

        {/* Search Bar Container */}
        <div
          sx={{
            bg: colors.modes["group"].primary,
            pb: 5,
            display: ab({ _: "block", md: "flex" }),
          }}
        >
          {/* Product Search */}
          <div
            sx={{
              display: "flex",
              pt: "44px",
              px: ab({ _: 3, md: 3 }),
              flexGrow: 7,
            }}
          >
            <div
              sx={{
                mt: "15px",
                mr: ab({ _: "0px", md: "24px" }),
                textAlign: "center",
                width: ab({ _: "32px", md: "64px" }),
                height: ab({ _: "32px", md: "64px" }),
                maxWidth: ab({ _: "32px", md: "64px" }),
                color: "#fff",
              }}
            >
              <SearchIcon style={{ fill: "#fff" }} />
            </div>
            <div
              sx={{
                flex: 2,
                alignSelf: "center",
                px: ab({ _: 2, md: 4 }),
              }}
            >
              <input
                type="text"
                placeholder={searchProductPlaceholder}
                onFocus={handleProductFocus}
                onChange={handleProductInputChange}
                onBlur={handleProductBlur}
                value={productInput.value}
                sx={{
                  width: "100%",
                  backgroundColor: "transparent",
                  border: 0,
                  color: "#fff",
                  fontSize: ab({ _: "14px", md: "24px" }),
                  lineHeight: 1.8,
                  outline: "none",
                  borderBottom: "1px solid #fff",
                }}
              />
            </div>
          </div>

          {/* Kennzeichnung Search */}
          <div
            sx={{
              display: "flex",
              py: "44px",
              px: ab({ _: 3, md: 3 }),
              flexGrow: 6,
              position: "relative",
              top: ab({ _: "0px", md: "20px" }),
              width: ab({ _: "96%", md: "auto" }),
              marginLeft: ab({ _: "32px", md: "auto" }),
            }}
          >
            <div
              sx={{
                flex: 2,
                alignSelf: "center",
                px: ab({ _: 2, md: 4 }),
              }}
            >
              <input
                type="text"
                placeholder={searchKennzeichnungPlaceholder}
                onFocus={handleKennzeichnungFocus}
                onChange={handleKennzeichnungInputChange}
                onBlur={handleKennzeichnungBlur}
                value={kennzeichnungInput.value}
                sx={{
                  width: "100%",
                  backgroundColor: "transparent",
                  border: 0,
                  color: "#fff",
                  fontSize: ab({ _: "14px", md: "24px" }),
                  lineHeight: 1.8,
                  outline: "none",
                  borderBottom: "1px solid #fff",
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Results Table */}
      <ProductDataEntries
        entries={filteredEntries}
        loading={loading}
        error={error}
        productPerPage={productPerPage}
        tableHeaderProduct={tableHeaderProduct}
        tableHeaderKennzeichnung={tableHeaderKennzeichnung}
        tableHeaderCertificate={tableHeaderCertificate}
        tableHeaderDate={tableHeaderDate}
        noResultsText={noResultsText}
        totalEntries={entries}
        kennzeichnungInput={kennzeichnungInput}
        productInput={productInput}
      >
        {children}
      </ProductDataEntries>
    </div>
  )
}

export default DatasourceSearch
